import { ChangeEvent, FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  Checkbox,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { EmployeePermissionFull } from 'src/api/permissions';
import { EmployeeInfo } from 'src/components/EmployeeInfo/EmployeeInfo';
import { PersonAutocomplete } from 'src/components/PersonAutocomplete/PersonAutocomplete';
import { Section } from 'src/components/Section/Section';
import { getPermissionsState } from 'src/store/permissions/selectors';
import { fetchEmployeePermissions } from 'src/store/permissions/services';
import { permissionsActions } from 'src/store/permissions/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { labelsByEmployeePermission } from '../consts';

interface PermissionCheckboxProps {
  permission: EmployeePermissionFull;
}

const PermissionCheckbox: FC<PermissionCheckboxProps> = ({ permission }) => {
  const dispatch = useAppDispatch();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        permissionsActions.setPermission({
          id: permission.permissionId,
          enabled: e.target.checked,
        }),
      );
    },
    [dispatch, permission.permissionId],
  );

  return (
    <Checkbox
      label={labelsByEmployeePermission[permission.permissionId]}
      checked={permission.enabled}
      disabled={!permission.isEditable}
      onChange={handleChange}
      dataTestId={`${permission.permissionId}Checkbox`}
    />
  );
};

export const AdministratorUserPermissionsFields: FC = () => {
  const dispatch = useAppDispatch();
  const { employee, permissions } = useSelector(getPermissionsState);

  const changeEmployee = useCallback(
    (value: Option<string>) => {
      dispatch(permissionsActions.setEmployee(value));
      dispatch(fetchEmployeePermissions(value.id as string));
    },
    [dispatch],
  );

  const clearPermissions = useCallback(() => {
    dispatch(permissionsActions.clearPermissions());
  }, [dispatch]);

  return (
    <>
      <Section title='Выберите сотрудника' data-testid='employeeSection'>
        <PersonAutocomplete
          type='select'
          key={employee?.id}
          value={employee}
          onChange={changeEmployee}
          onInputClear={clearPermissions}
          dataTestId='employee'
        />
        {!!employee?.id && (
          <EmployeeInfo
            employeeNumber={employee.id as string}
            testId='employeeInfo'
          />
        )}
      </Section>

      {!!permissions.length && !!employee?.id && (
        <Section title='Права' data-testid='employeeSection'>
          {permissions.map(permission => (
            <PermissionCheckbox
              key={permission.permissionId}
              permission={permission}
            />
          ))}
        </Section>
      )}
    </>
  );
};
